import classNames from 'classnames'
import { FC, ReactNode, useState } from 'react'
import createStyles from '@mui/styles/createStyles'
import makeStyles from '@mui/styles/makeStyles'
import FormControl from '@mui/material/FormControl'
import OutlinedInput, { OutlinedInputProps } from '@mui/material/OutlinedInput'
import useTranslation from '../lib/lang/useTranslation'
import { locationNumberPipe } from '@app/lib/masks/numberMask'
import { IconButton, Stack } from '@mui/material'
import Icon from './Icon'
import IMask from 'imask'
import { themeColors } from '@app/constants'

export interface CustomTextProps extends Omit<OutlinedInputProps, 'size'> {
	autocomplete?: boolean
	onBlur?: any
	onChange?: React.ChangeEventHandler<HTMLTextAreaElement | HTMLInputElement>
	classes?: {
		adornedEnd?: string
		adornedStart?: string
		root?: string
		input?: string
	}
	className?: string
	disabled?: boolean
	value?: string
	id?: string
	name?: string
	multiple?: boolean
	placeholder?: string
	readOnly?: boolean
	type?:
		| 'text'
		| 'file'
		| 'ip'
		| 'number'
		| 'integer'
		| 'decimal'
		| 'email'
		| 'password'
		| 'time'
		| 'youtubeLink'
		| 'location'
		| 'price'
		| 'uuid'
		| 'ipv4'
		| 'mac'
	size?: 'tiny' | 'small' | 'medium' | 'large'
	endAdornment?: ReactNode
	startAdornment?: ReactNode
	inputProps?: any
	InputProps?: any
	ref?: any
	inputRef?: any
	decimals?: boolean
	required?: boolean
	width?: string
	height?: string
	numOfDecimals?: number
	disableBorder?: boolean
	borderRadius?: string
}

const useStyles = makeStyles((theme) =>
	createStyles({
		textField: ({
			width,
			height,
			disableBorder,
			borderRadius,
		}: {
			width: string | undefined
			height: string | undefined
			disableBorder: boolean | undefined
			borderRadius: string | undefined
		}) => ({
			borderColor: disableBorder ? 'transparent' : theme.palette.divider, // Assuming themeColors is part of theme
			width: width, // Use the width prop if provided
			backgroundColor: theme.palette.common.white, // Assuming themeColors is part of theme
			borderRadius: borderRadius,
			'& .MuiOutlinedInput-root': {
				height: height,
				...(disableBorder
					? {
							'& .MuiOutlinedInput-notchedOutline': {
								borderColor: 'transparent',
							},
							'&:hover .MuiOutlinedInput-notchedOutline': {
								borderColor: 'transparent',
							},
							'&.Mui-focused .MuiOutlinedInput-notchedOutline': {
								borderColor: 'transparent',
							},
					  }
					: {
							'&.Mui-focused .MuiOutlinedInput-notchedOutline': {
								borderColor: themeColors.agitronDark,
							},
					  }),
			},
		}),
		smallSize: {
			fontSize: theme.typography.subtitle2.fontSize,
			paddingBottom: theme.spacing(1),
			paddingTop: theme.spacing(1),
			paddingLeft: theme.spacing(1.3),
			paddingRight: theme.spacing(1),
			maxHeight: '36px',
		},
	})
)

const CustomTextField: FC<CustomTextProps> = ({
	autocomplete = false,
	className,
	classes,
	disabled,
	name,
	placeholder,
	size = 'large',
	value = '',
	required = false,
	width = '100%',
	height: heightFromProps,
	type = 'text',
	onChange,
	numOfDecimals,
	disableBorder = false,
	inputProps,
	InputProps,
	endAdornment,
	...otherProps
}) => {
	const { t } = useTranslation()

	const height =
		heightFromProps ?? (otherProps.multiline ? undefined : '53px')

	const componentClasses = useStyles({
		width,
		height,
		disableBorder,
		borderRadius: otherProps.borderRadius,
	})
	const [showPassword, setShowPassword] = useState(false)

	if (placeholder) {
		placeholder = t(placeholder)
	}

	let outlinedProps: OutlinedInputProps = {
		name,
	}

	if (!autocomplete && name) {
		outlinedProps.autoComplete = `new-${name}`
	}

	const handleClickShowPassword = () => setShowPassword((show) => !show)

	const handleMouseDownPassword = (
		event: React.MouseEvent<HTMLButtonElement>
	) => {
		event.preventDefault()
	}

	const isPassword = type === 'password'

	return (
		<FormControl
			className={classNames(
				componentClasses.textField,
				className,
				classes?.root
			)}
			disabled={disabled}
			variant="outlined"
		>
			<OutlinedInput
				{...otherProps}
				{...outlinedProps}
				required={required}
				classes={{
					input: classNames(classes?.input, {
						[componentClasses.smallSize]:
							size === 'small' || size === 'tiny',
					}),
					adornedStart: classes?.adornedStart,
					adornedEnd: classes?.adornedEnd,
				}}
				onChange={(e) => {
					if (type === 'price') {
						let textValue = e.target.value

						if (/^[\d.,]+$/.test(textValue)) {
							textValue = textValue.replace(/[.,]/g, '')
							const numericValue = (
								parseFloat(textValue) / 100
							).toFixed(2)

							if (onChange) {
								onChange({
									...e,
									target: {
										...e.target,
										value: numericValue,
										name: name || '',
									},
								})
							}
						} else if (e.target.value === '') {
							if (onChange) {
								onChange({
									...e,
									target: {
										...e.target,
										value: '0.00',
										name: name || '',
									},
								})
							}
						}
					} else if (type === 'decimal' || type === 'integer') {
						let textValue = e.target.value

						if (textValue && textValue.includes(',')) {
							textValue = textValue.replace(/\,/g, '.')
						}

						const scale =
							type === 'integer' ? 0 : numOfDecimals ?? 2
						const val = IMask.pipe(textValue, {
							mask: Number,
							scale: scale,
							thousandsSeparator: '',
							radix: '.',
							normalizeZeros: false,
							padFractionalZeros: false,
						})

						if (onChange) {
							onChange({
								...e,
								target: {
									...e.target,
									value: val.toString(),
									// value: Number(val).toFixed(scale),
									name: name || '',
								},
							})
						}
					} else if (type === 'location') {
						let textValue = e.target.value

						if (textValue && textValue.includes(',')) {
							textValue = textValue.replace(/\,/g, '.')
						}

						const val = locationNumberPipe(textValue)

						if (onChange) {
							onChange({
								...e,
								target: {
									...e.target,
									value: val.toString(),
									name: name || '',
								},
							})
						}
					} else if (onChange) {
						onChange(e)
					}
				}}
				inputProps={{
					...inputProps,
					...InputProps,
					onInvalid: (e) => {
						if (type === 'email') {
							e.preventDefault()
						}
					},
					type: showPassword || !isPassword ? 'text' : 'password',
					height: '36px',
					maxheight: '36px',
				}}
				componentsProps={{
					root:
						size === 'tiny'
							? {
									style: {
										height: '36px',
										maxHeight: '36px',
									},
							  }
							: {},
				}}
				endAdornment={
					isPassword && !disabled ? (
						<Stack direction="row" alignItems="center" gap="20px">
							<IconButton
								aria-label="toggle password visibility"
								onClick={handleClickShowPassword}
								onMouseDown={handleMouseDownPassword}
								edge="end"
							>
								{showPassword ? (
									<Icon icon="eye_off" size="25px" />
								) : (
									<Icon icon="eye_on" size="25px" />
								)}
							</IconButton>

							{endAdornment}
						</Stack>
					) : (
						endAdornment ?? undefined
					)
				}
				disabled={disabled}
				placeholder={placeholder}
				value={value}
				type={type}
			/>
		</FormControl>
	)
}

export default CustomTextField
